import React, { useEffect, useRef } from "react";

import Button from "../../Button";

import style from "./styles.module.scss";

export type ModalSize = "small" | "medium" | "large";

export interface ModalProps {
  open: boolean;
  onClose(): void;
  title?: React.ReactNode;
  icon?: string;
  closeLabel?: string;
  actions?: React.ReactNode;
  headerActions?: React.ReactNode;
  size?: ModalSize;
  dialogClassName?: string;
  headerClassName?: string;
  dismissible?: boolean;
  children?: React.ReactNode;
}

const sizeClasses: { [key in ModalSize]: string } = {
  small: style.dialogSmall,
  medium: style.dialogMedium,
  large: style.dialogLarge,
};

let titleSequence = 0;

export default function Modal({
  open,
  onClose,
  title,
  icon,
  closeLabel,
  actions,
  headerActions,
  size = "medium",
  dialogClassName = "",
  headerClassName = "",
  dismissible = true,
  children,
}: ModalProps) {
  const titleId = useRef(`pgm-modal-title-${(titleSequence += 1)}`);
  const pressedOnScrim = useRef(false);

  useEffect(() => {
    if (!open || !dismissible) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, dismissible, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={style.scrim}
      role="presentation"
      onMouseDown={(event) => {
        pressedOnScrim.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (dismissible && pressedOnScrim.current && event.target === event.currentTarget) {
          onClose();
        }
        pressedOnScrim.current = false;
      }}
    >
      <div
        className={`${style.dialog} ${sizeClasses[size]} ${dialogClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId.current : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || closeLabel) && (
          <div className={`${style.header} ${headerClassName}`.trim()}>
            {icon && (
              <span
                className={`material-symbols-sharp ${style.icon}`}
                aria-hidden="true"
              >
                {icon}
              </span>
            )}
            {title && (
              <h2 id={titleId.current} className={`pgm-title-large ${style.title}`}>
                {title}
              </h2>
            )}
            {headerActions}
            {closeLabel && (
              <Button
                iconButton
                variant="text"
                size="small"
                aria-label={closeLabel}
                onClick={onClose}
                icon={<span className="material-symbols-sharp">close</span>}
              />
            )}
          </div>
        )}

        <div className={style.content}>{children}</div>

        {actions && <div className={style.actions}>{actions}</div>}
      </div>
    </div>
  );
}
